import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

/**
 * 動画と店舗の紐付けの確認キュー（E-3）。
 *
 * **規則ベースの自動判定は、確定されるまで公開側に出ない。**
 * 実測（2026-09-05）で、緩い一致は611本を誤爆させ、厳しい一致は6本しか取れない。
 * 自動判定だけでは決められないので、ここで1本ずつ人が決める。
 *
 * 2種類が並ぶ:
 * - **自動判定（未確認）** … いま訪問者には見えていない。確定するまでサイトの損
 * - **この列より前（legacy）** … いま見えている。誰かが見たのかどうかがデータに無い
 *
 * どちらも同じボタンで片付くが、急ぎ方が違うので状態を出している。
 */
export default function VideoLinkQueue() {
  useAuthGuard();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/video-links/queue`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems((body.data && body.data.items) || []);
    } catch (e) {
      setError('キューを読み込めませんでした: ' + e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (item, confirm) => {
    setBusy(item.id);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/video-links/${item.id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ confirm }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      setError('反映できませんでした: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  const hidden = (items || []).filter((i) => !i.published).length;

  return (
    <Layout>
      <div className="container-fluid py-3">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <h4 className="mb-0">動画の紐付け確認</h4>
          {items !== null && (
            <span className="badge bg-primary-subtle text-primary-emphasis">{items.length}件</span>
          )}
          {hidden > 0 && (
            <span className="badge bg-warning-subtle text-warning-emphasis">
              未公開 {hidden}件（自動判定。確定するまで店舗ページに出ません）
            </span>
          )}
          <button className="btn btn-phoenix-secondary btn-sm" type="button" onClick={load}>
            再読み込み
          </button>
        </div>

        {error && <div className="alert alert-danger py-2">{error}</div>}
        {items === null && <div className="text-muted">読み込み中...</div>}
        {items !== null && items.length === 0 && (
          <div className="text-muted">確認待ちの紐付けはありません</div>
        )}

        {(items || []).map((item) => (
          <div key={item.id} className="card mb-2">
            <div className="card-body d-flex flex-wrap gap-3">
              {/* 判断の材料は動画そのもの。サムネイルとタイトルだけで決まらない
                  ことが多いので、YouTube で開けるようにしておく */}
              <a
                href={`https://www.youtube.com/watch?v=${item.videoId}`}
                target="_blank"
                rel="noreferrer"
                className="flex-shrink-0"
              >
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt=""
                    style={{ width: 160, height: 90, objectFit: 'cover', borderRadius: 6 }}
                  />
                ) : (
                  <span
                    className="d-inline-flex align-items-center justify-content-center bg-light text-muted"
                    style={{ width: 160, height: 90, borderRadius: 6 }}
                  >
                    再生
                  </span>
                )}
              </a>

              <div className="flex-grow-1" style={{ minWidth: 240 }}>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <span
                    className={`badge ${
                      item.published
                        ? 'bg-secondary-subtle text-secondary-emphasis'
                        : 'bg-warning text-dark'
                    }`}
                  >
                    {item.sourceLabel}
                  </span>
                  {!item.published && (
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      いまは公開されていません
                    </span>
                  )}
                </div>
                <div className="fw-semibold" style={{ fontSize: '0.9375rem' }}>
                  {item.title}
                </div>
                <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                  {item.channelTitle}
                  {item.publishedAt ? ` / ${item.publishedAt}` : ''}
                </div>
                <div className="mt-1" style={{ fontSize: '0.8125rem' }}>
                  紐付け先: <a href={`/stores/${item.storeId}`}>{item.storeName}</a>
                  {item.storePrefecture && (
                    <span className="text-muted">（{item.storePrefecture}）</span>
                  )}
                </div>
              </div>

              <div className="d-flex flex-column gap-2 justify-content-center">
                <button
                  className="btn btn-success btn-sm"
                  type="button"
                  disabled={busy === item.id}
                  onClick={() => decide(item, true)}
                >
                  この店で合っている
                </button>
                <button
                  className="btn btn-phoenix-secondary btn-sm"
                  type="button"
                  disabled={busy === item.id}
                  onClick={() => decide(item, false)}
                >
                  紐付けを外す
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
